import React, { useState, useMemo } from 'react';
import { Pencil, Plus, ChevronDown, ChevronUp, X } from 'lucide-react';
import {
    IconButton,
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableHead,
    TableRow,
    CircularProgress,
    Collapse,
    Paper,
    InputAdornment,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent
} from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import useGroups from '../../../hooks/purchaseInvoices/useGroups.js';
import { useResponsive } from '../../../hooks/purchaseInvoices/useResponsive.js';
import {
    StyledContainer,
    StyledCard,
    StyledTableContainer,
    StyledTableRow,
    StyledTableCell,
    StyledTableHeadCell,
    softColors,
    activeStatusStyles
} from "../../../styles/purchaseInvoices/pages/commonTableStyles.js";
import ConfirmationDialog from '../../../components/purchaseInvoices/pages/ConfirmationDialog.js';
import groupsStyles from '../../../styles/purchaseInvoices/pages/groupsStyles.js';

const Groups = () => {
    const { isDesktop } = useResponsive();
    const [expandedRow, setExpandedRow] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const {
        groups,
        isLoading,
        error,
        addGroup,
        updateGroup,
        deleteGroup
    } = useGroups();

    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [groupToDelete, setGroupToDelete] = useState(null);
    const [newGroup, setNewGroup] = useState({ 
        name: '', 
        email: '', 
        defaultApprover: '', 
        isActive: true,
        autoApprovalEnabled: false,  
        autoApprovalMatchThreshold: 90,
        limitAmountEnabled: false,
        autoApprovalMaxAmount: '',
        autoApprovalDelayHours: 0,
        requiresAttentionDaysThreshold: 3,
        useOrderBasedValidation: false
    });
    const [editGroup, setEditGroup] = useState(null);

    const columns = [
        {
            accessorKey: 'name',
            header: 'שם קבוצה',
        },
        {
            accessorKey: 'email',
            header: 'מייל ראשי',
            cell: ({ value }) => value || '-'
        },
        {
            accessorKey: 'inboxEmail',
            header: 'תיבת מייל לקבלת חשבוניות',
            cell: ({ value }) => value || '-'
        },
        {
            accessorKey: 'defaultApprover',
            header: 'מאשר דיפולטיבי',
            cell: ({ value }) => value || '-'
        },
        {
            accessorKey: 'isActive',
            header: 'פעילה?',
            cell: ({ value }) => (
                <Typography 
                    variant="body2" 
                    sx={value ? activeStatusStyles.active : activeStatusStyles.inactive}
                >
                    {value ? 'כן' : 'לא'}
                </Typography>
            )
        },
        {
            accessorKey: 'requiresAttentionDaysThreshold',
            header: 'סף ימים להתראה',
            cell: ({ value }) => (
                <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#d32f2f' }}>
                    {value || 3} ימים
                </Typography>
            )
        },
        {
            id: 'autoApprovalLimits',
            header: 'הגבלות אישור אוטומטי',
            cell: ({ row }) => {
                if (!row.autoApprovalEnabled) return <span style={{color:'#b0b0b0'}}>כבוי</span>;
                const amount = row.autoApprovalMaxAmount;
                const threshold = row.autoApprovalMatchThreshold;
                const delay = row.autoApprovalDelayHours;
                return (
                    <Box sx={{display:'flex',flexDirection:'column',alignItems:'center'}}>
                        <Typography variant="body2" sx={{fontWeight:'bold',color:'#1976d2'}}>
                            {(amount && amount > 0) ? `עד ${amount.toLocaleString()} ₪` : 'ללא הגבלת סכום'}
                        </Typography>
                        <Typography variant="caption" sx={{color:'#555'}}>
                            שיוך שורות: <span style={{fontWeight:'bold'}}>{threshold}%</span>
                        </Typography>
                        <Typography variant="caption" sx={{color:'#555'}}>
                            {delay && delay > 0 ? `השהיה: ${delay} שעות` : 'ללא השהיה'}
                        </Typography>
                    </Box>
                );
            }
        },
        {
            id: 'actions',
            header: 'פעולות',
            cell: ({ row }) => (
                <Box display="flex" justifyContent="center" gap={1}>
                    <IconButton
                        aria-label="edit"
                        onClick={() => handleEdit(row)}
                        size="small"
                        sx={groupsStyles.editButtonIcon}
                    >
                        <Pencil size={18} color="#1976d2" />
                    </IconButton>
                    <IconButton
                        aria-label="delete"
                        onClick={() => handleDeleteClick(row._id, row.name)}
                        size="small"
                        sx={groupsStyles.deleteButtonIcon}
                    >
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )
        }
    ];

    const handleEdit = (group) => {
        setEditGroup({
            ...group,
            limitAmountEnabled: group.autoApprovalMaxAmount !== undefined && 
                               group.autoApprovalMaxAmount !== null && 
                               group.autoApprovalMaxAmount !== ''
        });
        setEditOpen(true);
    };

    const handleDeleteClick = (groupId, groupName) => {
        setGroupToDelete({ _id: groupId, name: groupName });
        setDeleteOpen(true);
    };

    const handleCancelDelete = () => {
        setDeleteOpen(false);
        setGroupToDelete(null);
    };

    const handleConfirmDelete = async () => {
        await deleteGroup(groupToDelete._id);
        setDeleteOpen(false);
        setGroupToDelete(null);
    };

    const renderMobileCard = (group) => {
        const isExpanded = expandedRow === group._id;

        return (
            <Paper key={group._id} elevation={1} sx={groupsStyles.mobileCard}>
                <Box sx={groupsStyles.mobileCardHeader}>
                    <Typography variant="subtitle1" fontWeight="bold">
                        {group.name}
                    </Typography>

                    <Box sx={groupsStyles.mobileCardButtonsContainer}>
                        <IconButton
                            aria-label="edit"
                            onClick={() => handleEdit(group)}
                            size="small"
                            sx={groupsStyles.editButtonIcon}
                        >
                            <Pencil size={18} color="#1976d2" />
                        </IconButton>
                        <IconButton
                            aria-label="delete"
                            onClick={() => handleDeleteClick(group._id, group.name)}
                            size="small"
                            sx={groupsStyles.deleteButtonIcon}
                        >
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Box>

                <Box>
                    <Typography variant="body2" color="text.secondary">מייל ראשי:</Typography>
                    <Typography variant="body1">{group.email || '-'}</Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">תיבת מייל ייעודית לחשבוניות:</Typography>
                    <Typography variant="body1">{group.inboxEmail || '-'}</Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">מאשר דיפולטיבי:</Typography>
                    <Typography variant="body1">{group.defaultApprover || '-'}</Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">אישור אוטומטי:</Typography>
                    {!group.autoApprovalEnabled ? (                        <Typography variant="body1" sx={{color:'#b0b0b0'}}>כבוי</Typography>
                    ) : null}

                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary">בדיקה מול הזמנת רכש (אלדן):</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: group.useOrderBasedValidation ? '#2e7d32' : '#b0b0b0' }}>
                                {group.useOrderBasedValidation ? 'פעיל' : 'לא פעיל'}
                            </Typography>
                        </Box>

                    {!group.autoApprovalEnabled ? (                        <Typography variant="body1" sx={{color:'#b0b0b0'}}>כבוי</Typography>
                    ) : (
                        <Box sx={{display:'flex',flexDirection:'column',alignItems:'flex-start'}}>
                            <Typography variant="body1" sx={{fontWeight:'bold',color:'#1976d2'}}>
                                {group.autoApprovalMaxAmount !== undefined && group.autoApprovalMaxAmount !== null && group.autoApprovalMaxAmount !== ''
                                    ? `עד ${group.autoApprovalMaxAmount.toLocaleString()} ₪`
                                    : 'ללא הגבלת סכום'}
                            </Typography>
                            <Typography variant="caption" sx={{color:'#555'}}>
                                שיוך שורות: <span style={{fontWeight:'bold'}}>{group.autoApprovalMatchThreshold}%</span>
                            </Typography>
                            <Typography variant="caption" sx={{color:'#555'}}>
                                {group.autoApprovalDelayHours && group.autoApprovalDelayHours > 0 ? `השהיה: ${group.autoApprovalDelayHours} שעות` : 'ללא השהיה'}
                            </Typography>
                        </Box>
                    )}
                </Box>

                <Box sx={{ mt: 2 }}>
                    <IconButton
                        size="small"
                        onClick={() => setExpandedRow(isExpanded ? null : group._id)}
                        sx={groupsStyles.expandButton}
                    >
                        {isExpanded ? <ChevronUp /> : <ChevronDown />}
                    </IconButton>
                    <Collapse in={isExpanded} timeout="auto">
                        <Box sx={groupsStyles.expandedContent}>
                            <Typography variant="body2" color="text.secondary">פעילה?</Typography>
                            <Typography variant="body1">{group.isActive ? 'כן' : 'לא'}</Typography>
                        </Box>
                    </Collapse>
                </Box>
            </Paper>
        );
    };

    const filteredGroups = useMemo(() => {
        if (!searchTerm.trim() || !groups?.length) {
            return groups || [];
        }

        const lowerSearchTerm = searchTerm.toLowerCase();
        return groups.filter(group =>
            group.name.toLowerCase().includes(lowerSearchTerm) ||
            (group.email && group.email.toLowerCase().includes(lowerSearchTerm))
        );
    }, [groups, searchTerm]);

    if (isLoading) {
        return (
            <Box sx={groupsStyles.loadingContainer}>
                <CircularProgress />
                <Typography variant="h6" color="textSecondary" sx={groupsStyles.loadingText}>
                    טוען נתונים...
                </Typography>
            </Box>
        );
    }

    return (
        <StyledContainer>
            <StyledCard>
                <Box sx={groupsStyles.headerBox}>
                    <Box sx={{ width: { xs: '100%', sm: '300px' } }}>
                        <TextField
                            placeholder="חיפוש קבוצה"
                            variant="outlined"
                            size="small"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'primary.main' }} />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{ dir: "rtl" }}
                            sx={groupsStyles.searchInput}
                        />
                    </Box>

                    <Button
                        onClick={() => {
                            setNewGroup({ 
                                name: '', 
                                email: '', 
                                defaultApprover: '', 
                                isActive: true,
                                autoApprovalEnabled: false,
                                autoApprovalMatchThreshold: 90,
                                limitAmountEnabled: false,
                                autoApprovalMaxAmount: '',
                                autoApprovalDelayHours: 0,
                                requiresAttentionDaysThreshold: 3 
                            });
                            setAddOpen(true);
                        }}
                        color="primary"
                        size="medium"
                        startIcon={<Plus size={20} strokeWidth={3} />}
                        sx={groupsStyles.addButtonResponsive}
                    >
                        הוספת קבוצה
                    </Button>
                </Box>

                {error ? (
                    <Box sx={groupsStyles.errorBox}>
                        <Typography variant="h6" color="error" sx={groupsStyles.errorText}>
                            שגיאה בטעינת הנתונים
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => window.location.reload()}
                        >
                            נסה שנית
                        </Button>
                    </Box>
                ) : isDesktop ? (
                    <StyledTableContainer sx={groupsStyles.tableMinHeight}>
                        <Table dir="rtl">
                            <TableHead>
                                <TableRow>
                                    {columns.map(column => (
                                        <StyledTableHeadCell
                                            key={column.id || column.accessorKey}
                                            align="center"
                                        >
                                            {column.header}
                                        </StyledTableHeadCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredGroups.length === 0 ? (
                                    <TableRow>
                                        <StyledTableCell colSpan={columns.length} align="center">
                                            <Typography variant="body1" color="text.secondary">
                                                {searchTerm ? "לא נמצאו תוצאות חיפוש" : "אין קבוצות להצגה"}
                                            </Typography>
                                        </StyledTableCell>
                                    </TableRow>
                                ) : (
                                    filteredGroups.map((row) => (
                                        <StyledTableRow key={row._id}>
                                            {columns.map((column, columnIndex) => (
                                                <StyledTableCell
                                                    key={columnIndex}
                                                    align="center"
                                                >
                                                    {column.cell && typeof column.cell === 'function' ?
                                                        column.cell({ row, value: row[column.accessorKey] }) :
                                                        row[column.accessorKey] || '-'
                                                    }
                                                </StyledTableCell>
                                            ))}
                                        </StyledTableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </StyledTableContainer>
                ) : (
                    <Box sx={groupsStyles.mobileCardContainer}>
                        {filteredGroups.length === 0 ? (
                            <Box sx={groupsStyles.emptyBox}>
                                <Typography variant="body1" color="text.secondary">
                                    {searchTerm ? "לא נמצאו תוצאות חיפוש" : "אין קבוצות להצגה"}
                                </Typography>
                            </Box>
                        ) : (
                            filteredGroups.map(group => renderMobileCard(group))
                        )}
                    </Box>
                )}

                <Box sx={groupsStyles.footerBox}>
                    <Typography variant="body2" color="text.secondary" align="center">
                        {filteredGroups.length > 0
                            ? `סה"כ ${filteredGroups.length} קבוצות`
                            : ''
                        }
                    </Typography>
                </Box>
            </StyledCard>

            <Dialog
                open={addOpen}
                onClose={() => {
                    setNewGroup({ 
                        name: '', 
                        email: '', 
                        defaultApprover: '', 
                        isActive: true,
                        autoApprovalEnabled: false,
                        autoApprovalMatchThreshold: 90,
                        limitAmountEnabled: false,
                        autoApprovalMaxAmount: '',
                        autoApprovalDelayHours: 0,
                        requiresAttentionDaysThreshold: 3 
                    });
                    setAddOpen(false);
                }}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: groupsStyles.dialog }}
            >
                <DialogTitle sx={groupsStyles.dialogTitle}>
                    <Typography sx={groupsStyles.dialogTitleText}>
                        הוספת קבוצה חדשה
                    </Typography>

                    <IconButton
                        onClick={() => {
                            setNewGroup({ 
                                name: '', 
                                email: '', 
                                defaultApprover: '', 
                                isActive: true,
                                autoApprovalEnabled: false,
                                autoApprovalMatchThreshold: 90,
                                limitAmountEnabled: false,
                                autoApprovalMaxAmount: '',
                                autoApprovalDelayHours: 0,
                                requiresAttentionDaysThreshold: 3 
                            });
                            setAddOpen(false);
                        }}
                        sx={groupsStyles.dialogCloseButton}
                        size="small"
                    >
                        <X size={20} strokeWidth={2.5} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={groupsStyles.dialogContent}>
                    <div style={groupsStyles.formContainer}>
                        <div>
                            <label style={groupsStyles.formLabel}>שם קבוצה:</label>
                            <input
                                type="text"
                                placeholder="שם הקבוצה"
                                style={groupsStyles.formInput}
                                value={newGroup.name}
                                onChange={(e) => setNewGroup(prev => ({ ...prev, name: e.target.value }))}
                            />
                        </div>
                        
                        <div>
                            <label style={groupsStyles.formLabel}>מייל ראשי:</label>
                            <input
                                type="email"
                                placeholder="הזן מייל ראשי"
                                style={groupsStyles.formInput}
                                value={newGroup.email || ""}
                                onChange={(e) => setNewGroup(prev => ({ ...prev, email: e.target.value }))}
                            />
                        </div>
                        
                        <div>
                            <label style={groupsStyles.formLabel}>תיבת מייל לקבלת חשבוניות:</label>
                            <input
                                type="email"
                                placeholder="למשל: invoices-supplier@neopharmgroup.com"
                                style={groupsStyles.formInput}
                                value={newGroup.inboxEmail || ""}
                                onChange={(e) => setNewGroup(prev => ({ ...prev, inboxEmail: e.target.value }))}
                            />
                        </div>
                        
                        <div>
                            <label style={groupsStyles.formLabel}>מאשר דיפולטיבי:</label>
                            <input
                                type="text"
                                placeholder="הזן שם משתמש Priority של המאשר"
                                style={groupsStyles.formInput}
                                value={newGroup.defaultApprover || ""}
                                onChange={(e) => setNewGroup(prev => ({ ...prev, defaultApprover: e.target.value }))}
                            />
                        </div>
                        
                        <div style={{ margin: '10px 0' }}>
                            <label style={{ marginLeft: 8 }}>פעילה:</label>
                            <input
                                type="checkbox"
                                checked={newGroup.isActive}
                                onChange={e => setNewGroup(prev => ({ ...prev, isActive: e.target.checked }))}
                                style={{ transform: 'scale(1.2)' }}
                            />
                        </div>
                        
                        <div style={{ marginTop: '15px' }}>
                            <label style={groupsStyles.formLabel}>
                                מספר ימים מקסימלי לטיפול ב-שגיאה בקליטה לפריוריטי:
                            </label>
                            <input
                                type="number"
                                min="1"
                                placeholder="3"
                                style={groupsStyles.formInput}
                                value={newGroup.requiresAttentionDaysThreshold || 3}
                                onChange={(e) => {
                                    const value = Math.max(1, Number(e.target.value));
                                    setNewGroup(prev => ({
                                        ...prev,
                                        requiresAttentionDaysThreshold: value
                                    }));
                                }}
                            />
                        </div>

                        <div style={{ 
                            margin: '20px 0', 
                            padding: '15px', 
                            backgroundColor: '#f0f4ff', 
                            borderRadius: '8px',
                            border: '1px solid #c8d8ff'
                        }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#5c35cc' }}>
                                ⚙️ הגדרות בדיקת חשבוניות
                            </Typography>

                            <div style={{ margin: '10px 0' }}>
                                <label style={{ marginLeft: 8, fontWeight: 500 }}>
                                    בדיקה מול הזמנת רכש ישירה (בלי קבלת סחורה):
                                </label>
                                <input
                                    type="checkbox"
                                    checked={newGroup.useOrderBasedValidation || false}
                                    onChange={e => setNewGroup(prev => ({ 
                                        ...prev, 
                                        useOrderBasedValidation: e.target.checked 
                                    }))}
                                    style={{ transform: 'scale(1.2)', marginRight: '8px' }}
                                />
                            </div>
                            {newGroup.useOrderBasedValidation && (
                                <Typography variant="caption" sx={{ color: '#5c35cc', display: 'block', mt: 0.5, mb: 1 }}>
                                    ⚠️ קבוצה זו תבדוק חשבוניות מול מספר הזמנת הרכש (PO) במקום מול קבלת סחורה
                                </Typography>
                            )}
                        </div>

                        <div style={{ 
                            margin: '20px 0', 
                            padding: '15px', 
                            backgroundColor: '#f0f8ff', 
                            borderRadius: '8px',
                            border: '1px solid #d0e8ff'
                        }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                                 הגדרות אישור אוטומטי
                            </Typography>
                            
                            <div style={{ margin: '10px 0' }}>
                                <label style={{ marginLeft: 8, fontWeight: 500 }}>
                                    אפשר אישור אוטומטי:
                                </label>
                                <input
                                    type="checkbox"
                                    checked={newGroup.autoApprovalEnabled}
                                    onChange={e => setNewGroup(prev => ({ 
                                        ...prev, 
                                        autoApprovalEnabled: e.target.checked 
                                    }))}
                                    style={{ transform: 'scale(1.2)', marginRight: '8px' }}
                                />
                            </div>

                            {newGroup.autoApprovalEnabled && (
                                <div style={{ marginTop: '15px' }}>
                                    <label style={groupsStyles.formLabel}>
                                        אחוזי התאמה מינימליים לשיוך שורות (%):
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        placeholder="90"
                                        style={groupsStyles.formInput}
                                        value={newGroup.autoApprovalMatchThreshold}
                                        onChange={(e) => {
                                            const value = Math.min(100, Math.max(0, Number(e.target.value)));
                                            setNewGroup(prev => ({ 
                                                ...prev, 
                                                autoApprovalMatchThreshold: value 
                                            }));
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        💡 אם כל שורות החשבונית יתאימו מעל <strong>{newGroup.autoApprovalMatchThreshold}%</strong> - תאושר אוטומטית
                                    </Typography>
                                    
                                    <div style={{ marginTop: '15px' }}>
                                        <label style={groupsStyles.formLabel}>
                                            <input
                                                type="checkbox"
                                                checked={newGroup.limitAmountEnabled}
                                                onChange={e => setNewGroup(prev => ({ 
                                                    ...prev, 
                                                    limitAmountEnabled: e.target.checked, 
                                                    autoApprovalMaxAmount: e.target.checked ? prev.autoApprovalMaxAmount : '' 
                                                }))}
                                                style={{ marginLeft: '8px', transform: 'scale(1.2)' }}
                                            />
                                            הגבל סכום לאישור אוטומטי
                                        </label>
                                        {newGroup.limitAmountEnabled && (
                                            <>
                                                <input
                                                    type="number"
                                                    min="0"
                                                    placeholder="הכנס סכום (₪)"
                                                    style={groupsStyles.formInput}
                                                    value={newGroup.autoApprovalMaxAmount}
                                                    onChange={(e) => {
                                                        const value = Math.max(0, Number(e.target.value));
                                                        setNewGroup(prev => ({
                                                            ...prev,
                                                            autoApprovalMaxAmount: value
                                                        }));
                                                    }}
                                                />
                                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                    💡 חשבוניות מעל סכום זה יעברו לאישור ידני
                                                </Typography>
                                            </>
                                        )}
                                    </div>
                                    
                                    <div style={{ marginTop: '15px' }}>
                                        <label style={groupsStyles.formLabel}>
                                            זמן השהיה לפני אישור אוטומטי (בשעות):
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            placeholder="0"
                                            style={groupsStyles.formInput}
                                            value={newGroup.autoApprovalDelayHours || 0}
                                            onChange={(e) => {
                                                const value = Math.max(0, Number(e.target.value));
                                                setNewGroup(prev => ({
                                                    ...prev,
                                                    autoApprovalDelayHours: value
                                                }));
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            אם תוגדר השהיה, החשבונית תאושר אוטומטית רק לאחר הזמן הזה (בשעות) אם לא אושרה ידנית.
                                        </Typography>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={groupsStyles.dialogActions}>
                            <Button
                                onClick={() => {
                                    setNewGroup({ 
                                        name: '', 
                                        email: '', 
                                        defaultApprover: '', 
                                        isActive: true,
                                        autoApprovalEnabled: false,
                                        autoApprovalMatchThreshold: 90,
                                        limitAmountEnabled: false,
                                        autoApprovalMaxAmount: '',
                                        autoApprovalDelayHours: 0,
                                        requiresAttentionDaysThreshold: 3,
                                        useOrderBasedValidation: false
                                    });
                                    setAddOpen(false);
                                }}
                                sx={groupsStyles.dialogCancelButton}
                            >
                                ביטול
                            </Button>
                            <Button
                                onClick={async () => {
                                    await addGroup(newGroup);
                                    setAddOpen(false);
                                    setNewGroup({ 
                                        name: '', 
                                        email: '', 
                                        defaultApprover: '', 
                                        isActive: true,
                                        autoApprovalEnabled: false,
                                        autoApprovalMatchThreshold: 90,
                                        limitAmountEnabled: false,
                                        autoApprovalMaxAmount: '',
                                        autoApprovalDelayHours: 0,
                                        requiresAttentionDaysThreshold: 3,
                                        useOrderBasedValidation: false
                                    });
                                }}
                                variant="contained"
                                sx={groupsStyles.dialogSubmitButton}
                            >
                                הוסף
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <Dialog
                open={editOpen}
                onClose={() => setEditOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: groupsStyles.dialog }}
            >
                <DialogTitle sx={groupsStyles.dialogTitle}>
                    <Typography sx={groupsStyles.dialogTitleText}>
                        עריכת קבוצה
                    </Typography>

                    <IconButton
                        onClick={() => setEditOpen(false)}
                        sx={groupsStyles.dialogCloseButton}
                        size="small"
                    >
                        <X size={20} strokeWidth={2.5} />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={groupsStyles.dialogContent}>
                    {editGroup && (
                        <div style={groupsStyles.formContainer}>
                            <div>
                                <label style={groupsStyles.formLabel}>שם קבוצה:</label>
                                <input
                                    type="text"
                                    placeholder="שם הקבוצה"
                                    style={groupsStyles.formInput}
                                    value={editGroup.name}
                                    onChange={(e) => setEditGroup(prev => ({ ...prev, name: e.target.value }))}
                                />
                            </div>
                            
                            <div>
                                <label style={groupsStyles.formLabel}>מייל ראשי:</label>
                                <input
                                    type="email"
                                    placeholder="הזן מייל ראשי"
                                    style={groupsStyles.formInput}
                                    value={editGroup.email || ""}
                                    onChange={(e) => setEditGroup(prev => ({ ...prev, email: e.target.value }))}
                                />
                            </div>
                            
                            <div>
                                <label style={groupsStyles.formLabel}>תיבת מייל לקבלת חשבוניות:</label>
                                <input
                                    type="email"
                                    placeholder="למשל: invoices-supplier@neopharmgroup.com"
                                    style={groupsStyles.formInput}
                                    value={editGroup.inboxEmail || ""}
                                    onChange={(e) => setEditGroup(prev => ({ ...prev, inboxEmail: e.target.value }))}
                                />
                            </div>
                            
                            <div>
                                <label style={groupsStyles.formLabel}>מאשר דיפולטיבי:</label>
                                <input
                                    type="text"
                                    placeholder="הזן שם משתמש Priority של המאשר"
                                    style={groupsStyles.formInput}
                                    value={editGroup.defaultApprover || ""}
                                    onChange={(e) => setEditGroup(prev => ({ ...prev, defaultApprover: e.target.value }))}
                                />
                            </div>
                            
                            <div style={{ margin: '10px 0' }}>
                                <label style={{ marginLeft: 8 }}>פעילה:</label>
                                <input
                                    type="checkbox"
                                    checked={editGroup.isActive}
                                    onChange={e => setEditGroup(prev => ({ ...prev, isActive: e.target.checked }))}
                                    style={{ transform: 'scale(1.2)' }}
                                />
                            </div>
                            
                            <div style={{ marginTop: '15px' }}>
                                <label style={groupsStyles.formLabel}>
                                    מספר ימים מקסימלי לטיפול בשגיאה בקליטה לפריוריטי:
                                </label>
                                <input
                                    type="number"
                                    min="1"
                                    placeholder="3"
                                    style={groupsStyles.formInput}
                                    value={editGroup.requiresAttentionDaysThreshold || 3}
                                    onChange={(e) => {
                                        const value = Math.max(1, Number(e.target.value));
                                        setEditGroup(prev => ({
                                            ...prev,
                                            requiresAttentionDaysThreshold: value
                                        }));
                                    }}
                                />
                            </div>

                            <div style={{ 
                                margin: '20px 0', 
                                padding: '15px', 
                                backgroundColor: '#f0f4ff', 
                                borderRadius: '8px',
                                border: '1px solid #c8d8ff'
                            }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#5c35cc' }}>
                                    ⚙️ הגדרות בדיקת חשבוניות
                                </Typography>

                                <div style={{ margin: '10px 0' }}>
                                    <label style={{ marginLeft: 8, fontWeight: 500 }}>
                                        בדיקה מול הזמנת רכש ישירה (בלי קבלת סחורה):
                                    </label>
                                    <input
                                        type="checkbox"
                                        checked={editGroup.useOrderBasedValidation || false}
                                        onChange={e => setEditGroup(prev => ({ 
                                            ...prev, 
                                            useOrderBasedValidation: e.target.checked 
                                        }))}
                                        style={{ transform: 'scale(1.2)', marginRight: '8px' }}
                                    />
                                </div>
                                {editGroup.useOrderBasedValidation && (
                                    <Typography variant="caption" sx={{ color: '#5c35cc', display: 'block', mt: 0.5, mb: 1 }}>
                                        ⚠️ קבוצה זו תבדוק חשבוניות מול מספר הזמנת הרכש (PO) במקום מול קבלת סחורה
                                    </Typography>
                                )}
                            </div>

                            <div style={{ 
                                margin: '20px 0', 
                                padding: '15px', 
                                backgroundColor: '#f0f8ff', 
                                borderRadius: '8px',
                                border: '1px solid #d0e8ff'
                            }}>
                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 'bold', color: '#1976d2' }}>
                                     הגדרות אישור אוטומטי
                                </Typography>
                                
                                <div style={{ margin: '10px 0' }}>
                                    <label style={{ marginLeft: 8, fontWeight: 500 }}>
                                        אפשר אישור אוטומטי:
                                    </label>
                                    <input
                                        type="checkbox"
                                        checked={editGroup.autoApprovalEnabled || false}
                                        onChange={e => setEditGroup(prev => ({ 
                                            ...prev, 
                                            autoApprovalEnabled: e.target.checked 
                                        }))}
                                        style={{ transform: 'scale(1.2)', marginRight: '8px' }}
                                    />
                                </div>

                                {editGroup.autoApprovalEnabled && (
                                    <div style={{ marginTop: '15px' }}>
                                        <label style={groupsStyles.formLabel}>
                                            אחוזי התאמה מינימליים לשיוך שורות (%):
                                        </label>
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            placeholder="90"
                                            style={groupsStyles.formInput}
                                            value={editGroup.autoApprovalMatchThreshold || 90}
                                            onChange={(e) => {
                                                const value = Math.min(100, Math.max(0, Number(e.target.value)));
                                                setEditGroup(prev => ({ 
                                                    ...prev, 
                                                    autoApprovalMatchThreshold: value 
                                                }));
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                            💡 אם כל שורות החשבונית יתאימו מעל <strong>{editGroup.autoApprovalMatchThreshold}%</strong> - תאושר אוטומטית
                                        </Typography>
                                        
                                        <div style={{ marginTop: '15px' }}>
                                            <label style={groupsStyles.formLabel}>
                                                <input
                                                    type="checkbox"
                                                    checked={editGroup.limitAmountEnabled || false}
                                                    onChange={e => setEditGroup(prev => ({ 
                                                        ...prev, 
                                                        limitAmountEnabled: e.target.checked, 
                                                        autoApprovalMaxAmount: e.target.checked ? prev.autoApprovalMaxAmount : '' 
                                                    }))}
                                                    style={{ marginLeft: '8px', transform: 'scale(1.2)' }}
                                                />
                                                הגבל סכום לאישור אוטומטי
                                            </label>
                                            {editGroup.limitAmountEnabled && (
                                                <>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        placeholder="הכנס סכום (₪)"
                                                        style={groupsStyles.formInput}
                                                        value={editGroup.autoApprovalMaxAmount || ''}
                                                        onChange={(e) => {
                                                            const value = Math.max(0, Number(e.target.value));
                                                            setEditGroup(prev => ({
                                                                ...prev,
                                                                autoApprovalMaxAmount: value
                                                            }));
                                                        }}
                                                    />
                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                        💡 חשבוניות מעל סכום זה יעברו לאישור ידני
                                                    </Typography>
                                                </>
                                            )}
                                        </div>
                                        
                                        <div style={{ marginTop: '15px' }}>
                                            <label style={groupsStyles.formLabel}>
                                                זמן השהיה לפני אישור אוטומטי (בשעות):
                                            </label>
                                            <input
                                                type="number"
                                                min="0"
                                                placeholder="0"
                                                style={groupsStyles.formInput}
                                                value={editGroup.autoApprovalDelayHours || 0}
                                                onChange={(e) => {
                                                    const value = Math.max(0, Number(e.target.value));
                                                    setEditGroup(prev => ({
                                                        ...prev,
                                                        autoApprovalDelayHours: value
                                                    }));
                                                }}
                                            />
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                אם תוגדר השהיה, החשבונית תאושר אוטומטית רק לאחר הזמן הזה (בשעות) אם לא אושרה ידנית.
                                            </Typography>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div style={groupsStyles.dialogActions}>
                                <Button
                                    onClick={() => setEditOpen(false)}
                                    sx={groupsStyles.dialogCancelButton}
                                >
                                    ביטול
                                </Button>
                                <Button
                                    onClick={async () => {
                                        await updateGroup(editGroup._id, editGroup);
                                        setEditOpen(false);
                                        setEditGroup(null);
                                    }}
                                    variant="contained"
                                    sx={groupsStyles.dialogSubmitButton}
                                >
                                    עדכון
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <ConfirmationDialog
                entityName={groupToDelete?.name || ""}
                entityType="קבוצה"
                open={deleteOpen}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                isPending={false}
            />
        </StyledContainer>
    );
};

export default Groups;